import { useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { useDashboard } from '../contexts/DashboardContext'
import {
  HomeIcon,
  UserGroupIcon,
  DocumentTextIcon,
  ChartBarIcon,
  CogIcon,
  Bars3Icon,
  XMarkIcon,
  ShieldCheckIcon,
  CpuChipIcon,
  ComputerDesktopIcon,
  TicketIcon,
  ArrowRightOnRectangleIcon,
  ChevronRightIcon,
  BellAlertIcon,
  GlobeAltIcon,
  HandRaisedIcon,
} from '@heroicons/react/24/outline'

const getNavigation = (user) => {
  const baseNav = [
    { name: 'Dashboard', href: '/dashboard', icon: HomeIcon },
    { name: 'Customers', href: '/dashboard/customers', icon: UserGroupIcon, badgeKey: 'total_users' },
    { name: 'Referral Approvals', href: '/dashboard/referrals', icon: TicketIcon, badgeKey: 'pending_referrals', badgeColor: 'bg-amber-500' },
    { name: 'Tenants', href: '/dashboard/tenants', icon: DocumentTextIcon },
    { name: 'RAG Configuration', href: '/dashboard/rag-config', icon: CpuChipIcon },
    { name: 'Trial Reminders', href: '/dashboard/trial-reminders', icon: BellAlertIcon },
    { name: 'Multilingual', href: '/dashboard/multilingual', icon: GlobeAltIcon },
    { name: 'Handover', href: '/dashboard/handover', icon: HandRaisedIcon },
    { name: 'Analytics', href: '/dashboard/analytics', icon: ChartBarIcon },
    { name: 'System Monitor', href: '/dashboard/system-monitor', icon: ComputerDesktopIcon },
    { name: 'Settings', href: '/dashboard/settings', icon: CogIcon },
  ]

  if (user?.is_superuser || user?.role === 'super_admin') {
    baseNav.splice(-2, 0, { name: 'Admin Users', href: '/dashboard/admin-users', icon: ShieldCheckIcon })
  }

  return baseNav
}

export default function Layout({ children }) {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const { user, logout } = useAuth()
  const { stats } = useDashboard()
  const location = useLocation()
  const navigation = getNavigation(user)

  const currentPage = navigation.find(n => location.pathname === n.href)?.name || 'Dashboard'

  return (
    <div className="h-screen bg-slate-50 flex overflow-hidden">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 w-64 bg-sidebar transform transition-transform duration-200 ease-in-out lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between h-16 px-5 border-b border-white/10">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 rounded-lg bg-brand-500 flex items-center justify-center">
                <span className="text-white font-bold text-sm">C</span>
              </div>
              <div>
                <span className="text-white font-semibold text-lg tracking-tight">CognaAssist</span>
              </div>
            </div>
            <button
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden text-slate-400 hover:text-white"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
            <p className="px-3 text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-3">
              Admin Panel
            </p>
            {navigation.map((item) => {
              const Icon = item.icon
              const isActive = location.pathname === item.href
              const badgeValue = item.badgeKey && stats?.[item.badgeKey]
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={() => setSidebarOpen(false)}
                  className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-150 group ${
                    isActive
                      ? 'bg-brand-600 text-white shadow-lg shadow-brand-600/25'
                      : 'text-slate-400 hover:text-white hover:bg-sidebar-hover'
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    <Icon className={`h-5 w-5 flex-shrink-0 ${
                      isActive ? 'text-white' : 'text-slate-500 group-hover:text-slate-300'
                    }`} />
                    <span>{item.name}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    {badgeValue > 0 && (
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full text-white font-medium ${item.badgeColor || 'bg-brand-500'}`}>
                        {badgeValue}
                      </span>
                    )}
                    {isActive && (
                      <ChevronRightIcon className="h-3.5 w-3.5 text-white/70" />
                    )}
                  </div>
                </Link>
              )
            })}
          </nav>

          {/* User section */}
          <div className="px-3 py-4 border-t border-white/10">
            <div className="flex items-center space-x-3 px-3 py-2">
              <div className="w-9 h-9 rounded-full bg-brand-600 flex items-center justify-center flex-shrink-0">
                <span className="text-white text-sm font-semibold">
                  {(user?.first_name || user?.email || 'A')[0].toUpperCase()}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">
                  {user?.first_name ? `${user.first_name} ${user.last_name || ''}`.trim() : 'Admin'}
                </p>
                <p className="text-xs text-slate-500 truncate">{user?.email}</p>
              </div>
            </div>
            <button
              onClick={logout}
              className="w-full flex items-center space-x-3 px-3 py-2 mt-1 rounded-lg text-sm text-slate-500 hover:text-red-400 hover:bg-sidebar-hover transition-colors"
            >
              <ArrowRightOnRectangleIcon className="h-5 w-5" />
              <span>Sign Out</span>
            </button>
          </div>
        </div>
      </aside>

      {/* Main content area */}
      <div className="flex-1 flex flex-col min-w-0 lg:ml-64 h-screen overflow-hidden">
        {/* Top header bar */}
        <header className="sticky top-0 z-30 h-16 bg-white border-b border-slate-200 flex items-center justify-between px-4 sm:px-6 lg:px-8">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden text-slate-500 hover:text-slate-700"
            >
              <Bars3Icon className="h-6 w-6" />
            </button>
            <div>
              <h1 className="text-lg font-semibold text-slate-900">{currentPage}</h1>
            </div>
          </div>

          <div className="flex items-center space-x-3">
            <div className="hidden sm:flex items-center space-x-2 text-xs text-slate-500">
              <div className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse" />
              <span>Online</span>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 p-4 sm:p-6 lg:p-8 overflow-y-auto min-h-0">
          <div className="max-w-7xl mx-auto animate-fade-in">
            {children}
          </div>
        </main>
      </div>
    </div>
  )
}
